<template>
  <component-wrapper class="smart-recruiters-detail">
    <error-message v-if="errors">
      {{ errors }}
    </error-message>

    <loading-indicator
      v-if="isLoading && !errors"
      variant="spinnerLocal"
    />

    <grid-container-fi v-if="hasPost">
      <div class="smart-recruiters-detail__section">
        <div class="smart-recruiters-detail__headline">
          <headline-fi :size="60">
            {{ post?.name || '' }}
          </headline-fi>
        </div>
        <div class="smart-recruiters-detail__sub-header">
          <span class="smart-recruiters-detail__entry-type">
            {{ entryType }}
          </span>
          <div class="smart-recruiters-detail__ref-number">
            {{ $dict.get('smart-Recruiters.Print-Job-Ref-Number', [post.refNumber]) }}
          </div>
        </div>
      </div>
    </grid-container-fi>

    <template v-if="hasPost">
      <grid-container-fi>
        <div class="smart-recruiters-detail__section">
          <section-one :sorted-custom-fields="sortedCustomFields" />

          <section-two
            :job-description="post.jobAd.sections.jobDescription.text"
            :job-qualification="post.jobAd.sections.qualifications.text"
          />

          <section-three
            v-if="benefitsPackageId"
            :benefits-package-id="benefitsPackageId"
            :fields="fields"
          />
        </div>
      </grid-container-fi>

      <grid-container-fi>
        <div class="fi-col-span-full">
          <section-four
            v-if="post.jobAd.sections.videos"
            :job-videos="post.jobAd.sections.videos"
            :job-info-text="fields.videoText?.value ? fields.videoText.value : ''"
          />
        </div>
      </grid-container-fi>

      <grid-container-fi>
        <div class="smart-recruiters-detail__section">
          <section-five />

          <section-six :company-description="post.jobAd.sections.companyDescription.text" />

          <section-seven
            :job-id="post.id"
            :job-apply-url="post.applyUrl"
            :job-additional-information="
              post.jobAd.sections.additionalInformation
                ? post.jobAd.sections.additionalInformation.text
                : ''
            "
          />
        </div>
      </grid-container-fi>
    </template>
  </component-wrapper>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { ErrorMessage, HeadlineFi, LoadingIndicator } from 'atoms';
import { computed, onMounted, onServerPrefetch, watch } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import {
  benefitsId,
  departmentId,
  entryTypeId,
  fieldOfActivityId,
} from '@/components/smart-recruiters/CustomFields';
import SectionFive from '@/components/smart-recruiters/detail/SectionFive.vue';
import SectionFour from '@/components/smart-recruiters/detail/SectionFour.vue';
import SectionOne from '@/components/smart-recruiters/detail/SectionOne.vue';
import SectionSeven from '@/components/smart-recruiters/detail/SectionSeven.vue';
import SectionSix from '@/components/smart-recruiters/detail/SectionSix.vue';
import SectionThree from '@/components/smart-recruiters/detail/SectionThree.vue';
import SectionTwo from '@/components/smart-recruiters/detail/SectionTwo.vue';
import { useFields } from '@/composables/fields';
import { useFischer } from '@/composables/fischerPlugin';
import GET_SMART_RECRUITERS_POST from '@/graphql/getSmartRecruitersPost.gql';

defineOptions({ name: 'SmartRecruitersDetailPage' });

const fields = useFields('SmartRecruitersDetailPageFields');

const fischer = useFischer();

const language = fischer.siteLanguage();

const readPostingId = () => {
  let pathname: string;
  if (IS_SHOWROOM) {
    pathname = fischer.query('urlMock') ?? '';
    if (!pathname) {
      pathname = `${window.location}/test-${Number(743999720550606)}`;
    }
    [, pathname] = /(?:^https?:\/\/[^/]*)?([^?]+)/.exec(pathname) as RegExpExecArray;
  } else {
    pathname = fischer.uri();
  }
  const match = /.*\/[^/]*-([\d]+)$/.exec(pathname);
  return match ? match[1] : '';
};

/** Read from the URL, so the server and the first client render agree without any fetch. */
const postingId = readPostingId();

/**
 * Keep this a reactive query and keep every value the template reads *derived* from `data`. An
 * awaited `client.query(...).toPromise()` cannot fill render state in time: a promise resolves in a
 * microtask, hydration is synchronous, and the server ships the finished page - so a client that
 * starts empty mismatches every node of it. An SSR result is restored synchronously during setup,
 * which is why these computeds need no watcher (see createSsrExchange).
 */
const { data, error, fetching, executeQuery } = useQuery<any, any>({
  query: GET_SMART_RECRUITERS_POST,
  variables: { language, postingId },
  pause: !postingId,
});

const post = computed<Record<string, any>>(() => data.value?.getSmartRecruitersPost ?? {});

/** What the template gates on: `post` falls back to `{}` and is never falsy on its own. */
const hasPost = computed(() => Boolean(data.value?.getSmartRecruitersPost));

/** The three failures worth a message: no id in the URL, a query error, an empty post. */
const errors = computed(() =>
  !postingId || error.value || (data.value !== undefined && !hasPost.value)
    ? fields.invalidPostId?.value
    : false,
);

const isLoading = computed(() => !hasPost.value && !errors.value);

/**
 * The rendered message is not enough on its own - PROD does not always show it - so a failure has to
 * reach the console too.
 *
 * Call this only where the result is settled. That is one place per environment: on the server after
 * onServerPrefetch has awaited the query, on the client when `fetching` falls, or at mount for a
 * result the ssrExchange restored during setup. Exactly one of them fires per failure.
 *
 * Not a `flush: 'sync'` watcher, unlike the rest of this file: useQuery assigns `data` before
 * `error` within one result, so a sync watcher woken by the `data` write reports "No post data"
 * while the real cause is one statement away from being readable.
 */
const logFailure = () => {
  if (fetching.value || hasPost.value) return;
  console.error(
    'Post error: ',
    error.value ?? new Error(postingId ? 'No post data' : 'No posting id'),
  );
};

watch(fetching, () => logFailure());
onMounted(logFailure);

const entryType = computed(() => {
  const entry = post.value?.customField?.find((field: any) => field.fieldId === entryTypeId);
  return entry?.valueLabel;
});

const benefitsPackageId = computed(() => {
  if (!post.value.customField) {
    return null;
  }
  const benefitField = post.value.customField.find((field: any) => field.fieldId === benefitsId);
  if (!benefitField) {
    return null;
  }
  return benefitField.valueId;
});

/** Returns null for a field the detail page does not show, COUNTRY among them. */
const mapCustomField = (field: string, label: string) => {
  switch (field) {
    case entryTypeId:
      return {
        order: 1,
        title: fields.tagExperienceLevel?.value,
        label,
        class: 'smart-recruiters-detail__section-one--experience-level',
        icon: 'user',
      };
    case fieldOfActivityId:
      return {
        order: 2,
        title: fields.tagFieldOfWork?.value,
        label,
        class: 'smart-recruiters-detail__section-one--field-of-work',
        icon: 'briefcase',
      };
    case departmentId:
      return {
        order: 3,
        title: fields.tagCorporateDivision?.value,
        label,
        class: 'smart-recruiters-detail__section-one--corporate-division',
        icon: 'signpost',
      };
    default:
      return null;
  }
};

const customFields = computed<any[]>(() => {
  const postCustomFields = post.value.customField;
  if (!postCustomFields) {
    return [];
  }

  return [
    {
      order: 0,
      title: fields.tagLocation?.value,
      label: post.value.location?.city,
      class: 'smart-recruiters-detail__section-one--location',
      icon: 'map-pin',
    },
    ...postCustomFields
      .map((element: any) => mapCustomField(element.fieldId, element.valueLabel))
      .filter(Boolean),
  ];
});

const sortedCustomFields = computed(() => {
  const compare = (a: any, b: any) => {
    if (a.order < b.order) return -1;
    if (a.order > b.order) return 1;
    return 0;
  };
  return [...customFields.value].sort(compare);
});

onServerPrefetch(async () => {
  if (postingId) {
    await executeQuery();
  }
  logFailure();
});
</script>

<style scoped src="./smart-recruiters-detail-page" />
